import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { events, FetchEventOptionsResponseOptions } from '../../api/swagger'
import { useAppSnackbar } from '../useAppSnackbar'
import useLoadingContext from '../useLoadingContext'
import { useLoadingIds } from '../useLoadingIds'
import { getEventTypes, getLeadershipTrainingRoles } from './helpers'

interface UseFetchEventOptionsProps {
  includeRoles: boolean
}
export interface UseFetchEventOptionsHook {
  eventTypes: Record<string, { id: string; name: string }[]>
  leadershipTrainingRoles: string[]
  triggerFetch: () => void
  isLoading: boolean
}

/**
 * Fetches event metadata used by the events filter (`EventsTab`) and the event form (`EventInformationFields`).
 * Callers toggle `includeRoles` when they need leadership training roles for the registration selector.
 */
export const useFetchEventOptions = ({
  includeRoles,
}: UseFetchEventOptionsProps): UseFetchEventOptionsHook => {
  const { handleError } = useAppSnackbar()
  const { t } = useTranslation()

  const {
    Events: { fetchEventOptions: fetchEventOptionsLoadingId },
  } = useLoadingIds()

  const [eventOptions, setEventOptions] =
    useState<FetchEventOptionsResponseOptions>({
      eventTypes: [],
      leadershipTrainingRoles: [],
    })

  const defaultErrorMessage = t(
    'Events.UseFetchEventOptions.Error.Default',
    'An unknown error occurred requesting event options.'
  )

  const fetchEventOptions = async (): Promise<void> => {
    try {
      const response = await events.fetchEventOptions({
        includeRoles,
      })

      setEventOptions(response.options)
    } catch (error) {
      await handleError(error, defaultErrorMessage)
    }
  }

  const { triggerFetch, isLoading } = useLoadingContext({
    loadingId: fetchEventOptionsLoadingId,
    asyncFunction: fetchEventOptions,
  })

  return {
    leadershipTrainingRoles: getLeadershipTrainingRoles(
      eventOptions.leadershipTrainingRoles
    ),
    eventTypes: getEventTypes(eventOptions.eventTypes),
    triggerFetch,
    isLoading,
  }
}
